import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import EmailIcon from '@material-ui/icons/Email';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import WorkIcon from '@material-ui/icons/Work';
import SchoolIcon from '@material-ui/icons/School';
import { fetchCandidateProfileView } from '../../redux/features/candidateProfileView/candidateProfileViewSlice';
import {
  trackPublicCandidateProfileViewed,
  trackCandidateLinkedInClicked,
  trackCandidateEmailClicked
} from '../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    paddingBottom: theme.spacing(4)
  },
  heroSection: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3)
  },
  heroBanner: {
    height: 200,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    position: 'relative'
  },
  heroContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    position: 'relative',
    marginTop: -80
  },
  profileAvatarSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }
  },
  profileAvatar: {
    width: 160,
    height: 160,
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper,
    fontSize: '3rem',
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  profileInfo: {
    flex: 1,
    paddingBottom: theme.spacing(2)
  },
  profileName: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary
  },
  profileHeadline: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontSize: '1.125rem'
  },
  profileMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  profileMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem'
  },
  profileMetaIcon: {
    fontSize: '1rem',
    color: theme.palette.text.hint
  },
  contentContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(0, 3)
  },
  sectionCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1]
  },
  sectionHeader: {
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary
  },
  sectionContent: {
    padding: theme.spacing(3)
  },
  aboutText: {
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.primary
  },
  experienceItem: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  experienceIcon: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  experienceIconSvg: {
    fontSize: '1.5rem',
    color: theme.palette.primary.main
  },
  experienceContent: {
    flex: 1
  },
  experienceTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5)
  },
  experienceCompany: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5)
  },
  experienceDate: {
    color: theme.palette.text.hint,
    fontSize: '0.8125rem',
    marginBottom: theme.spacing(1)
  },
  experienceDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    fontSize: '0.875rem'
  },
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  skillChip: {
    fontWeight: 500,
    fontSize: '0.875rem',
    height: 36,
    '& .MuiChip-label': {
      padding: theme.spacing(0, 1.5)
    }
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    gap: theme.spacing(2)
  }
}));

const getUserInitials = (fullName) => {
  if (!fullName) return '?';
  const names = fullName.trim().split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  }
  return fullName[0].toUpperCase();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month] = dateString.split('-');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  return `${monthNames[parseInt(month) - 1]} ${year}`;
};

const CandidateProfileView = () => {
  const classes = useStyles();
  const { userId } = useParams();
  const dispatch = useDispatch();

  // Get data from Redux store
  const {
    viewedProfile,
    viewedCandidate,
    viewedCandidatePhoto,
    loading,
    error
  } = useSelector((state) => state.candidateProfileView);

  useEffect(() => {
    console.log('👁️ [CandidateProfileView] Component mounted, userId:', userId);

    if (userId && !viewedProfile && !loading && !error) {
      // Fetch the profile view data
      dispatch(fetchCandidateProfileView(userId));
    }
  }, [viewedProfile, error, loading, userId, dispatch]);

  // Track profile view when data is loaded
  useEffect(() => {
    if (viewedProfile && userId) {
      const candidateName =
        viewedCandidate?.fullName ||
        viewedCandidate?.full_name ||
        viewedCandidate?.email ||
        'Unknown Candidate';

      const hasExperience = (viewedProfile.experiences || []).length > 0;
      const hasSkills = (viewedProfile.skills || []).length > 0;
      const experienceCount = (viewedProfile.experiences || []).length;
      const skillCount = (viewedProfile.skills || []).length;

      // Track the public profile view
      trackPublicCandidateProfileViewed(
        userId,
        candidateName,
        'direct_link', // Could be enhanced to track actual source
        hasExperience,
        hasSkills,
        experienceCount,
        skillCount
      );
    }
  }, [viewedProfile, userId, viewedCandidate]);

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            Loading candidate profile...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <Typography variant="h6" color="error">
            Error: {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!viewedProfile) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <Typography variant="h6" color="textSecondary">
            Profile not found
          </Typography>
        </Box>
      </Box>
    );
  }

  // Extract candidate name from multiple possible sources
  const candidateName =
    viewedCandidate?.fullName ||
    viewedCandidate?.full_name ||
    viewedCandidate?.email ||
    'Candidate';

  const userInitials = getUserInitials(candidateName);
  const profile = viewedProfile;
  const photoUrl = viewedCandidatePhoto || '';

  console.log('👁️ [CandidateProfileView] Rendering with:', {
    candidateName,
    profile,
    photoUrl
  });

  return (
    <Box className={classes.root}>
      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <Box className={classes.heroBanner} />
        <Box className={classes.heroContent}>
          <Box className={classes.profileAvatarSection}>
            <Avatar src={photoUrl} className={classes.profileAvatar}>
              {!photoUrl && userInitials}
            </Avatar>
            <Box className={classes.profileInfo}>
              <Typography variant="h3" className={classes.profileName}>
                {candidateName}
              </Typography>
              <Typography variant="h6" className={classes.profileHeadline}>
                {profile?.headline || 'No headline provided'}
              </Typography>
              <Box className={classes.profileMeta}>
                {profile?.currentPosition && (
                  <Box className={classes.profileMetaItem}>
                    <WorkIcon className={classes.profileMetaIcon} />
                    <span>{profile.currentPosition}</span>
                  </Box>
                )}
                {profile?.education && (
                  <Box className={classes.profileMetaItem}>
                    <SchoolIcon className={classes.profileMetaIcon} />
                    <span>{profile.education}</span>
                  </Box>
                )}
                {(profile?.city || profile?.location) && (
                  <Box className={classes.profileMetaItem}>
                    <LocationOnIcon className={classes.profileMetaIcon} />
                    <span>
                      {[profile?.city, profile?.location]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </Box>
                )}
                {profile?.contactEmail && (
                  <Box className={classes.profileMetaItem}>
                    <EmailIcon className={classes.profileMetaIcon} />
                    <a
                      href={`mailto:${profile.contactEmail}`}
                      onClick={() =>
                        trackCandidateEmailClicked(userId, candidateName)
                      }
                      style={{ color: 'inherit', textDecoration: 'none' }}>
                      {profile.contactEmail}
                    </a>
                  </Box>
                )}
                {profile?.linkedinUrl && (
                  <Box className={classes.profileMetaItem}>
                    <LinkedInIcon className={classes.profileMetaIcon} />
                    <a
                      href={profile.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackCandidateLinkedInClicked(userId, candidateName)
                      }
                      style={{ color: 'inherit', textDecoration: 'none' }}>
                      LinkedIn Profile
                    </a>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className={classes.contentContainer}>
        {/* About Section */}
        {profile?.about && (
          <Card className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>About</Typography>
            </Box>
            <Box className={classes.sectionContent}>
              <Typography className={classes.aboutText}>
                {profile.about}
              </Typography>
            </Box>
          </Card>
        )}

        {/* Experience Section */}
        {profile?.experiences && profile.experiences.length > 0 && (
          <Card className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Experience
              </Typography>
            </Box>
            <Box className={classes.sectionContent}>
              {profile.experiences.map((exp) => (
                <Box key={exp.id} className={classes.experienceItem}>
                  <Box className={classes.experienceIcon}>
                    <WorkIcon className={classes.experienceIconSvg} />
                  </Box>
                  <Box className={classes.experienceContent}>
                    <Typography className={classes.experienceTitle}>
                      {exp.title}
                    </Typography>
                    <Typography className={classes.experienceCompany}>
                      {exp.company}
                      {exp.employmentType && ` • ${exp.employmentType}`}
                    </Typography>
                    <Typography className={classes.experienceDate}>
                      {formatDate(exp.startDate)} -{' '}
                      {exp.isCurrentRole ? 'Present' : formatDate(exp.endDate)}
                      {exp.location && ` • ${exp.location}`}
                      {exp.locationType && ` • ${exp.locationType}`}
                    </Typography>
                    {exp.description && (
                      <Typography className={classes.experienceDescription}>
                        {exp.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        )}

        {/* Skills Section */}
        {profile?.skills && profile.skills.length > 0 && (
          <Card className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>Skills</Typography>
            </Box>
            <Box className={classes.sectionContent}>
              <Box className={classes.skillsContainer}>
                {profile.skills.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.skillName}
                    className={classes.skillChip}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default CandidateProfileView;
